import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { UserCheck, ShieldCheck, Timer, Euro, MapPin, Building2 } from "lucide-react";
import { Reveal, MaskedLine } from "./motion/Reveal";

const WHY_ICONS = [UserCheck, ShieldCheck, Timer, Euro, MapPin, Building2];
const WHY_KEYS = ["electrician", "certified", "fast", "pricing", "region", "audience"];

export default function WhyUs() {
  const { t } = useTranslation();
  return (
    <section id="waarom" data-testid="whyus-section" className="section bg-[#0A1128] relative overflow-hidden">
      {/* Faint editorial grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="mb-14 max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="accent-bar" />
              <span className="text-[#FF5A1F] uppercase font-bold tracking-widest text-xs">
                {t("why.eyebrow")}
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white mb-5 leading-[1.02]">
            <span className="block overflow-hidden"><MaskedLine text={t("why.title_1")} wordDelay={0.06} /></span>
            <span className="block overflow-hidden text-[#FF5A1F]"><MaskedLine text={t("why.title_2")} wordDelay={0.05} delay={0.15} /></span>
          </h2>
          <Reveal delay={0.15}>
            <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-2xl">
              {t("why.subtitle")}
            </p>
          </Reveal>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px 0px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {WHY_KEYS.map((k, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <motion.div
                key={k}
                data-testid={`why-card-${i}`}
                variants={{ hidden: { opacity: 0, y: 32 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } }}
                whileHover={{ y: -6 }}
                className="group grid-card-dark p-7 md:p-8 relative"
              >
                <span className="absolute top-4 right-5 text-[10px] font-black text-white/10 tabular-nums">
                  0{i + 1}
                </span>
                <div className="w-14 h-14 bg-[#FF5A1F] flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
                  <Icon className="w-7 h-7 text-white" strokeWidth={2.4} />
                </div>
                <h3 className="font-display text-xl md:text-[22px] text-white mb-3 leading-tight">
                  {t(`why.items.${k}.title`)}
                </h3>
                <p className="text-white/70 leading-relaxed text-sm">
                  {t(`why.items.${k}.desc`)}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
